import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type ValidationRulesBuilderService from '@/services/validation/validationRulesBuilderService'
import type { WeekDaysTimeRangeList, WeekDaysTimeRangeListsFieldConfig } from '@/services/forms/types'
import type { TypedFieldConfig } from '@/services/forms/baseForm/typedFormBuilder/types'
import { getRangeOverlappingIndexes } from '@/utils/rangeOverlapping'
import { DATE_FORMATES, type WeekDaysNumber } from '@/services/types'
import type DateTimeService from '@/services/dateTimeService'
import type { DateRange } from '@/services/tables/filters/types'
import type {
  WeekDaysTimeRangeFieldParams,
  WeekDaysTimeRangeFieldValidatorParams,
} from '@/services/forms/fieldBuilders/types'
import TmLogicError from '@/core/error/tmLogicError'

export type StartEndRangeErrorPayload = {
  ranges: {
    dayIndex: number
    rangeIndex: number
  }[]
}

export type DayRangeOverlapErrorPayload = {
  ranges: {
    dayIndex: number
    rangeIndex: number
  }[]
}

export const startEndRangeRuleName = 'weekDaysTimeRangeStartEndRange'
export const dayRangeOverlapRuleName = 'weekDaysTimeRangeDayRangeOverlap'
export const requiredRuleName = 'weekDaysTimeRangeRequired'

@injectable()
export default class WeekDaysTimeRangeFieldBuilder {
  constructor(
    @inject(SERVICE_TYPES.ValidationRulesBuilderService)
    protected readonly validationRulesBuilderService: ValidationRulesBuilderService,
    @inject(SERVICE_TYPES.DateTimeService) protected readonly dateTimeService: DateTimeService,
  ) {}

  public getWeekDaysTimeRangeFieldConfig(
    params?: WeekDaysTimeRangeFieldParams,
  ): TypedFieldConfig<WeekDaysTimeRangeListsFieldConfig> {
    return {
      fieldType: 'weekDaysTimeRangeLists',
      value: [] as WeekDaysTimeRangeList[],
      componentProps: {
        canSelectAnyRange: () => true,
        defaultTimeRange: () => this.getDefaultRange(),
        ...(params?.maxRanges ? { maxRanges: () => params.maxRanges! } : {}),
      },
      validators: this.getWeekDaysTimeRangeFieldValidators(params?.validatorParams),
    }
  }

  public getWeekDaysTimeRangeFieldValidators(params?: WeekDaysTimeRangeFieldValidatorParams) {
    return this.validationRulesBuilderService
      .createRules<WeekDaysTimeRangeList[]>()
      .custom(startEndRangeRuleName, (dayList) => {
        if (params?.isDisabled?.()) {
          return true
        }

        const payload: StartEndRangeErrorPayload = {
          ranges: [],
        }
        dayList.forEach((day, dayIndex) => {
          if (!day?.enabled) {
            return
          }
          day?.ranges.forEach((range, rangeIndex) => {
            if (range[1] <= range[0]) {
              payload.ranges.push({
                dayIndex,
                rangeIndex,
              })
            }
          })
        })
        if (payload.ranges.length) {
          return {
            message: {
              path: 'fieldWeekDaysTimeRangeLists.errors.endTimeBeforeStartTime',
            },
            payload,
          }
        }
        return true
      })
      .custom(dayRangeOverlapRuleName, (dayList) => {
        if (params?.isDisabled?.()) {
          return true
        }

        const payload: DayRangeOverlapErrorPayload = {
          ranges: [],
        }
        dayList.forEach((day, dayIndex) => {
          if (!day?.enabled) {
            return
          }
          getRangeOverlappingIndexes(day.ranges).forEach((rangeIndex) => {
            payload.ranges.push({
              dayIndex,
              rangeIndex,
            })
          })
        })
        if (payload.ranges.length) {
          return {
            message: {
              path: 'fieldWeekDaysTimeRangeLists.errors.intervalsOverlap',
            },
            payload,
          }
        }
        return true
      })
  }

  protected getDefaultRange() {
    return ['09:00', '17:00'].map((time) =>
      this.dateTimeService.getDateFromStringFormat(time, DATE_FORMATES.DATEPICKER_TIME_FORMAT),
    ) as DateRange
  }

  public mapScheduledIndexToWeekDaysNumber(index: number): WeekDaysNumber {
    if (index < 0) {
      throw new TmLogicError(`Index should be >= 0. Value = "${index}"`)
    }
    if (index > 6) {
      throw new TmLogicError(`Index should be <= 6. Value = "${index}"`)
    }
    return (index + 1) as WeekDaysNumber
  }

  public mapWeekDaysNumberToScheduledIndex(day: WeekDaysNumber): number {
    return day - 1
  }
}
